'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { trackLeadFormSubmission, trackScheduleMeetingClick, trackFormStart } from '@/lib/analytics';
import { containsNonLatinText, NON_ENGLISH_TEXT_ERROR } from '@/lib/text-validation';

interface ContactFormProps {
  /** CTA button text */
  buttonText?: string;
  /** Source tracking params to pass to HubSpot */
  trackingParams?: {
    src?: string;
    id?: string;
    cat?: string;
  };
}

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  problemType: string;
  message: string;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  company?: string;
  message?: string;
}

// Free-text fields checked for non-English (non-Latin script) input
const TEXT_FIELDS = ['fullName', 'company', 'message'] as const;

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm({ buttonText = 'Talk to an Expert', trackingParams }: ContactFormProps) {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    problemType: '',
    message: '',
  });
  const [problemTypeManuallyChanged, setProblemTypeManuallyChanged] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  // "Did you mean ...?" correction surfaced when the email looks like a typo.
  const [emailSuggestion, setEmailSuggestion] = useState('');

  // Honeypot field for spam protection
  const [honeypot, setHoneypot] = useState('');
  const [formStarted, setFormStarted] = useState(false);

  // Timestamp when component mounted (for server-side timing check)
  const [loadTime] = useState(() => Date.now());

  // Track form start on first field focus
  const handleFormFocus = () => {
    if (!formStarted) {
      setFormStarted(true);
      trackFormStart('Contact Form', 'contact_page');
    }
  };

  // Auto-select problem type based on tracking params
  useEffect(() => {
    if (problemTypeManuallyChanged || !trackingParams) return;

    const cat = (trackingParams.cat || '').toLowerCase();
    const id = (trackingParams.id || '').toLowerCase();

    let autoType = '';

    // Check cat param first
    if (cat && !autoType) {
      if (/security|cryptography|password/.test(cat)) {
        autoType = 'Security Assessment';
      } else if (/compliance|hipaa|gdpr|pci|soc2/.test(cat)) {
        autoType = 'Compliance Audit (SOC 2, HIPAA, PCI, etc.)';
      } else if (/cloud|azure|aws|gcp/.test(cat)) {
        autoType = 'Cloud Security';
      } else if (/ai|claude|copilot/.test(cat)) {
        autoType = 'AI Security & Governance';
      } else if (/patching|windows|update/.test(cat)) {
        autoType = 'Patching & Update Issues';
      } else if (/edr|crowdstrike|endpoint/.test(cat)) {
        autoType = 'EDR / Endpoint Security Migration';
      }
    }

    // Check id param if no match yet
    if (!autoType && id) {
      if (id.includes('24-7-detection-and-response')) {
        autoType = 'EDR / Endpoint Security Migration';
      } else if (id.includes('ai-security-audit')) {
        autoType = 'AI Security & Governance';
      } else if (id.includes('managed-services')) {
        autoType = 'Patching & Update Issues';
      }
    }

    if (autoType) {
      setFormData((prev) => ({ ...prev, problemType: autoType }));
    }
  }, [trackingParams, problemTypeManuallyChanged]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Please tell us how we can help';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Please add a little more detail (at least 10 characters)';
    }

    // Block non-English (non-Latin script) input in free-text fields
    for (const field of TEXT_FIELDS) {
      if (containsNonLatinText(formData[field])) {
        newErrors[field] = NON_ENGLISH_TEXT_ERROR;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check - if filled, it's a bot
    if (honeypot) {
      // Pretend success to not alert the bot
      setStatus('success');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setStatus('submitting');
    setErrorMessage('');
    setEmailSuggestion('');

    try {
      // Split full name into first/last for HubSpot
      const nameParts = formData.fullName.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      // Prepend problem type to message if selected
      const messageWithTopic = formData.problemType
        ? `[Topic: ${formData.problemType}]\n\n${formData.message}`
        : formData.message;

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          message: messageWithTopic,
          formName: 'Contact Us',
          ...trackingParams,
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
          _t: loadTime,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        if (typeof data.suggestion === 'string' && data.suggestion) {
          setEmailSuggestion(data.suggestion);
        }
        throw new Error(data.error || 'Failed to submit form');
      }

      // Track conversion in GA4 and Clarity (includes identification)
      const submittedEmail = formData.email;
      trackLeadFormSubmission('Contact Us', 'contact_page', undefined, submittedEmail);

      setStatus('success');
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        problemType: '',
        message: '',
      });
    } catch (error) {
      setStatus('error');
      const msg = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
      setErrorMessage(msg);

      // Track form error in Clarity
      if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
        window.clarity('set', 'contact_form', 'error');
      }
    }
  };

  // Apply a "Did you mean ...?" email correction, then let the user resubmit.
  const applySuggestion = () => {
    setFormData((prev) => ({ ...prev, email: emailSuggestion }));
    setEmailSuggestion('');
    setErrors((prev) => ({ ...prev, email: undefined }));
    setStatus('idle');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Track manual changes to problem type
    if (name === 'problemType') {
      setProblemTypeManuallyChanged(true);
    }

    // Drop a stale typo suggestion once the user edits the email themselves.
    if (name === 'email' && emailSuggestion) {
      setEmailSuggestion('');
    }

    // Warn immediately if the user types non-English (non-Latin script) characters
    if ((TEXT_FIELDS as readonly string[]).includes(name) && containsNonLatinText(value)) {
      setErrors((prev) => ({ ...prev, [name]: NON_ENGLISH_TEXT_ERROR }));
      return;
    }

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  if (status === 'success') {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/40 rounded-full mb-6">
          <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">Thank You!</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          We&apos;ve received your message and will get back to you the same business day.
        </p>

        {/* Schedule Now CTA */}
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-100 dark:border-blue-900 rounded-xl p-6 mb-6">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Want to skip the wait? Schedule a call with us right now.
          </p>
          <a
            href="https://hs.inventivehq.com/meetings/sean1324/contactpage"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackScheduleMeetingClick('contact_form_success')}
            className="inline-flex items-center justify-center gap-2 bg-[#2813e8] hover:bg-[#1f0fb8] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            Schedule a Call Now
          </a>
        </div>

        <button
          onClick={() => setStatus('idle')}
          className="text-[#2813e8] hover:text-[#2cc3d1] font-medium transition-colors"
        >
          Submit another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" name="Contact Us">
      {/* Honeypot field - hidden from users, visible to bots */}
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input
          type="text"
          id="website"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      {/* Full Name */}
      <div>
        <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Full name<span className="text-red-600 dark:text-red-400">*</span>
        </label>
        <input
          type="text"
          id="fullName"
          name="fullName"
          autoComplete="name"
          value={formData.fullName}
          onChange={handleChange}
          onFocus={handleFormFocus}
          aria-invalid={!!errors.fullName}
          aria-describedby={errors.fullName ? 'fullName-error' : undefined}
          className={`w-full px-4 py-3 rounded-lg border ${
            errors.fullName
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 dark:border-gray-600 focus:ring-[#2813e8] focus:border-[#2813e8]'
          } focus:outline-none focus:ring-2 transition-colors`}
          placeholder="John Doe"
        />
        {errors.fullName && (
          <p id="fullName-error" className="mt-1 text-sm text-red-700 dark:text-red-300 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.fullName}
          </p>
        )}
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Email<span className="text-red-600 dark:text-red-400">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          autoComplete="email"
          value={formData.email}
          onChange={handleChange}
          onFocus={handleFormFocus}
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? 'email-error' : undefined}
          className={`w-full px-4 py-3 rounded-lg border ${
            errors.email
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 dark:border-gray-600 focus:ring-[#2813e8] focus:border-[#2813e8]'
          } focus:outline-none focus:ring-2 transition-colors`}
          placeholder="john@company.com"
        />
        {errors.email && (
          <p id="email-error" className="mt-1 text-sm text-red-700 dark:text-red-300 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.email}
          </p>
        )}
      </div>

      {/* Phone */}
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Phone number <span className="text-gray-600 dark:text-gray-400 font-normal">(Optional)</span>
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          autoComplete="tel"
          value={formData.phone}
          onChange={handleChange}
          onFocus={handleFormFocus}
          className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-[#2813e8] focus:border-[#2813e8] focus:outline-none focus:ring-2 transition-colors"
          placeholder="(555) 123-4567"
        />
      </div>

      {/* Company */}
      <div>
        <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Company name <span className="text-gray-600 dark:text-gray-400 font-normal">(Optional)</span>
        </label>
        <input
          type="text"
          id="company"
          name="company"
          autoComplete="organization"
          value={formData.company}
          onChange={handleChange}
          onFocus={handleFormFocus}
          aria-invalid={!!errors.company}
          aria-describedby={errors.company ? 'company-error' : undefined}
          className={`w-full px-4 py-3 rounded-lg border ${
            errors.company
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 dark:border-gray-600 focus:ring-[#2813e8] focus:border-[#2813e8]'
          } focus:outline-none focus:ring-2 transition-colors`}
          placeholder="Acme Inc."
        />
        {errors.company && (
          <p id="company-error" className="mt-1 text-sm text-red-700 dark:text-red-300 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.company}
          </p>
        )}
      </div>

      {/* Problem Type */}
      <div>
        <label htmlFor="problemType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          What are you trying to solve? <span className="text-gray-600 dark:text-gray-400 font-normal">(Optional)</span>
        </label>
        <select
          id="problemType"
          name="problemType"
          value={formData.problemType}
          onChange={handleChange}
          onFocus={handleFormFocus}
          className={`w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-[#2813e8] focus:border-[#2813e8] focus:outline-none focus:ring-2 transition-colors ${
            formData.problemType ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          <option value="" className="text-gray-500 dark:text-gray-400">Select a topic...</option>
          <option value="Patching & Update Issues" className="text-gray-900 dark:text-gray-100">Patching & Update Issues</option>
          <option value="EDR / Endpoint Security Migration" className="text-gray-900 dark:text-gray-100">EDR / Endpoint Security Migration</option>
          <option value="AI Security & Governance" className="text-gray-900 dark:text-gray-100">AI Security & Governance</option>
          <option value="Compliance Audit (SOC 2, HIPAA, PCI, etc.)" className="text-gray-900 dark:text-gray-100">Compliance Audit (SOC 2, HIPAA, PCI, etc.)</option>
          <option value="Security Assessment" className="text-gray-900 dark:text-gray-100">Security Assessment</option>
          <option value="Cloud Security" className="text-gray-900 dark:text-gray-100">Cloud Security</option>
          <option value="Managed IT Services" className="text-gray-900 dark:text-gray-100">Managed IT Services</option>
          <option value="Other" className="text-gray-900 dark:text-gray-100">Other</option>
        </select>
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Message<span className="text-red-600 dark:text-red-400">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          onFocus={handleFormFocus}
          rows={4}
          aria-invalid={!!errors.message}
          aria-describedby={errors.message ? 'message-error' : undefined}
          className={`w-full px-4 py-3 rounded-lg border ${
            errors.message
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 dark:border-gray-600 focus:ring-[#2813e8] focus:border-[#2813e8]'
          } focus:outline-none focus:ring-2 transition-colors resize-y`}
          placeholder="Briefly describe your current security challenge or the service you're interested in..."
        />
        {errors.message && (
          <p id="message-error" className="mt-1 text-sm text-red-700 dark:text-red-300 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.message}
          </p>
        )}
      </div>

      {/* Error message */}
      {status === 'error' && (
        <div role="alert" className="p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-700 dark:text-red-300 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 dark:text-red-300 font-medium">Failed to submit</p>
            <p className="text-red-700 dark:text-red-300 text-sm">{errorMessage}</p>
            {emailSuggestion && (
              <p className="mt-2 text-sm text-red-700 dark:text-red-300">
                Did you mean{' '}
                <button
                  type="button"
                  onClick={applySuggestion}
                  className="font-semibold text-[#2813e8] underline hover:text-[#2cc3d1] transition-colors"
                >
                  {emailSuggestion}
                </button>
                ?
              </p>
            )}
          </div>
        </div>
      )}

      {/* Submit button */}
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full px-8 py-4 bg-[#2813e8] hover:bg-[#1f0fb8] disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        {status === 'submitting' ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            {buttonText}
          </>
        )}
      </button>
      <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
        We respect your privacy. No spam, ever. View our{' '}
        <Link href="/privacy-policy" className="underline hover:text-gray-600 dark:hover:text-gray-400 transition-colors">
          Privacy Policy
        </Link>.
      </p>
    </form>
  );
}
